import { BACKEND_URL } from "@/lib/backendUrl";
import { useCallback, useEffect, useState } from "react";
import { Banknote, Code2, Copy, CreditCard, Globe2, KeyRound, Loader2, Save, Share2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Chip, Loading, Panel } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";
import { dateTime } from "@/lib/format";

const OK = "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300";
const WARN = "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300";

const COUNTRY_CURRENCY = {
  GB: "GBP", IE: "EUR", US: "USD", CA: "USD", AU: "AUD", NZ: "AUD", IN: "INR", NG: "NGN",
  GH: "NGN", ZA: "ZAR", KE: "KES", SA: "SAR", AE: "AED", KW: "AED", QA: "SAR",
};

const Field = ({ field, value, disabled, onChange }) => (
  <div className="space-y-1.5">
    <Label className="text-[11px] text-muted-foreground">{field.label}</Label>
    {field.type === "switch" ? (
      <div className="flex h-9 items-center">
        <Switch checked={Boolean(value)} disabled={disabled}
                data-testid={`payments-${field.key}-input`}
                onCheckedChange={(v) => onChange(v)} />
      </div>
    ) : field.type === "select" ? (
      <Select value={String(value ?? "")} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger data-testid={`payments-${field.key}-input`} className="bg-muted/40 text-[12px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="max-h-64">
          {field.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    ) : (
      <Input value={value ?? ""} placeholder={field.placeholder} disabled={disabled}
             type={field.type === "number" ? "number" : "text"}
             data-testid={`payments-${field.key}-input`}
             onChange={(e) => onChange(field.type === "number" ? Number(e.target.value) : e.target.value)}
             className="bg-muted/40 font-mono text-[12px]" />
    )}
    {field.help && <p className="text-[10.5px] text-muted-foreground/80">{field.help}</p>}
  </div>
);

export const PaymentSettings = () => {
  const [meta, setMeta] = useState(null);
  const [tenant, setTenant] = useState({});
  const [secrets, setSecrets] = useState({});
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const res = await api.get("/payments/config");
    setMeta(res.data);
    setTenant(res.data.tenant);
    setSecrets({});
  }, []);

  useEffect(() => { load().catch(() => setMeta(false)); }, [load]);

  const saveTenant = async () => {
    setBusy("tenant");
    try {
      const res = await api.put("/payments/tenant", { values: tenant });
      toast[res.data?.pending_approval ? "message" : "success"](
        res.data?.message || "Payment routing and settlement saved");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const savePlatform = async () => {
    setBusy("platform");
    try {
      const values = {};
      Object.entries(secrets).forEach(([provider, fields]) => {
        const filled = Object.fromEntries(Object.entries(fields).filter(([, v]) => v !== ""));
        if (Object.keys(filled).length) values[provider] = filled;
      });
      if (!Object.keys(values).length) { toast.error("Nothing to store"); setBusy(""); return; }
      await api.put("/payments/platform", { values });
      toast.success("Provider credentials stored encrypted");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  if (!meta) return <Loading label="Loading payment settings" />;
  const groups = [...new Set(meta.tenant_fields.map((f) => f.group))];
  const giveUrl = `${window.location.origin}/give`;
  const qrUrl = `${BACKEND_URL}/api/public/give/qr.png?url=${encodeURIComponent(giveUrl)}`;
  const shareText = "Every gift is tracked to a named fund and receipted. Please give what you can:";
  const embed = `<a href="${giveUrl}" onclick="window.open(this.href,'give','width=520,height=760');return false">Donate</a>`;
  const copy = async (value, message) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(message);
    } catch {
      toast.error("Your browser blocked the clipboard — select the text and copy it by hand");
    }
  };

  return (
    <div className="space-y-4">
      <Panel testId="panel-payments-routing" kicker="Routing" title="Who takes the next gift">
        {(meta.pending_changes || []).length > 0 && (
          <div className="border-b border-amber-500/30 bg-amber-500/10 px-4 py-2.5 text-[11.5px] text-amber-700 dark:text-amber-300"
               data-testid="payments-pending-approval">
            {meta.pending_changes.length} change{meta.pending_changes.length > 1 ? "s" : ""} to these
            settings {meta.pending_changes.length > 1 ? "are" : "is"} awaiting the Board Secretary or
            the President. The settings below are the ones in force until then —
            see the <strong>Pending changes</strong> tab.
          </div>
        )}
        <div className="flex flex-wrap items-center gap-4 p-4" data-testid="payments-active">
          <div className="flex items-center gap-2">
            <Globe2 className="h-4 w-4 text-primary" />
            <span className="text-[12.5px] text-foreground">
              {meta.active.country} → <strong>{meta.active.label}</strong>
            </span>
            <Chip className={meta.active.configured ? OK : WARN}>
              {meta.active.configured ? "live" : "not configured"}
            </Chip>
          </div>
          <span className="text-[11.5px] text-muted-foreground">
            Charging in {meta.active.currency} · captured gifts are{" "}
            {meta.active.posting_mode === "auto"
              ? "posted and receipted immediately"
              : "held for finance to confirm"}
          </span>
        </div>
        <div className="grid grid-cols-1 gap-2 border-t border-border p-4 sm:grid-cols-2">
          {meta.providers.map((p) => (
            <div key={p.key} data-testid={`payments-provider-${p.key}`}
                 className="flex items-start justify-between gap-3 rounded-xl border border-border bg-muted/20 p-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-3.5 w-3.5 text-primary" />
                  <span className="text-[12.5px] font-medium text-foreground">{p.label}</span>
                  {p.active && <Chip className="border-primary/40 bg-primary/10 text-primary">routing here</Chip>}
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">{p.regions}</p>
              </div>
              <Chip className={p.configured ? OK : WARN}>
                {p.configured ? "keyed" : "not configured"}
              </Chip>
            </div>
          ))}
        </div>
      </Panel>

      <Panel testId="panel-payments-tenant" kicker="Tenant Admin"
             title="Routing, settlement account and posting"
             actions={meta.tenant_editable && (
               <Button size="sm" onClick={saveTenant} disabled={busy === "tenant"}
                       data-testid="payments-tenant-save"
                       className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                 {busy === "tenant" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                   : <Save className="mr-1 h-3.5 w-3.5" />} Save
               </Button>
             )}>
        {groups.map((group) => (
          <div key={group} className="border-b border-border last:border-0">
            <div className="flex items-center gap-1.5 px-4 pt-4 text-[11px] uppercase tracking-wide text-muted-foreground">
              {group === "Settlement" ? <Banknote className="h-3.5 w-3.5" /> : <Globe2 className="h-3.5 w-3.5" />}
              {group}
            </div>
            <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-3">
              {meta.tenant_fields.filter((f) => f.group === group).map((f) => (
                <Field key={f.key} field={f} value={tenant[f.key]} disabled={!meta.tenant_editable}
                       onChange={(v) => setTenant(f.key === "country"
                         ? { ...tenant, country: v, currency: COUNTRY_CURRENCY[v] || tenant.currency }
                         : { ...tenant, [f.key]: v })} />
              ))}
            </div>
          </div>
        ))}
        <div className="border-t border-border px-4 py-2.5 text-[11px] text-muted-foreground">
          The settlement account is where the provider pays your charity out. Donors' money never
          sits with us — it moves from their card to the account named here.
        </div>
      </Panel>

      <Panel testId="panel-payments-share" kicker="Share" title="Your give page">
        <div className="space-y-3 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <Input readOnly value={giveUrl} data-testid="give-link-input"
                   className="max-w-md bg-muted/40 font-mono text-[12px]" />
            <Button size="sm" variant="outline" data-testid="give-link-copy"
                    onClick={() => copy(giveUrl, "Give link copied")}
                    className="h-8 rounded-full border-border text-[11px]">
              <Copy className="mr-1 h-3.5 w-3.5" /> Copy link
            </Button>
            <Button size="sm" variant="outline" data-testid="give-share-whatsapp"
                    onClick={() => copy(`${shareText} ${giveUrl}`, "Share message copied")}
                    className="h-8 rounded-full border-border text-[11px]">
              <Share2 className="mr-1 h-3.5 w-3.5" /> Copy share message
            </Button>
            <Button size="sm" variant="outline" data-testid="give-embed-copy"
                    onClick={() => copy(embed, "Popup snippet copied")}
                    className="h-8 rounded-full border-border text-[11px]">
              <Code2 className="mr-1 h-3.5 w-3.5" /> Copy popup snippet
            </Button>
          </div>
          <div className="flex flex-wrap items-start gap-4">
            <img src={qrUrl} alt="Give page QR code" data-testid="give-qr"
                 className="h-32 w-32 rounded-xl border border-border bg-white p-2" />
            <div className="max-w-md space-y-2 text-[11.5px] text-muted-foreground">
              <p>
                Print the QR on a leaflet, banner or collection tin. It opens the same page, so
                every gift is tracked, receipted and audited like any other.
              </p>
              <p>
                Add <span className="font-mono">?src=whatsapp</span> or{" "}
                <span className="font-mono">/give/WINTER26</span> to a link and the register shows
                exactly what that channel or campaign raised.
              </p>
              <pre className="overflow-x-auto rounded-lg border border-border bg-muted/30 p-2 font-mono text-[10.5px] text-foreground">
{embed}
              </pre>
            </div>
          </div>
        </div>
      </Panel>

      <Panel testId="panel-payments-platform" kicker="Platform Master Admin"
             title="Provider API credentials"
             actions={meta.platform.configurable && (
               <Button size="sm" onClick={savePlatform} disabled={busy === "platform"}
                       data-testid="payments-platform-save"
                       className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                 {busy === "platform" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                   : <KeyRound className="mr-1 h-3.5 w-3.5" />} Store credentials
               </Button>
             )}>
        {!meta.platform.configurable && (
          <div className="p-4 text-[11.5px] text-muted-foreground" data-testid="payments-platform-readonly">
            Provider credentials are held by the Platform Master Admin. You can see whether a
            provider is live, never the keys.
          </div>
        )}
        <div className="space-y-4 p-4">
          {meta.providers.map((p) => (
            <div key={p.key} className="rounded-xl border border-border p-3">
              <div className="flex items-center gap-2">
                <span className="text-[12.5px] font-medium text-foreground">{p.label}</span>
                <Chip className={p.configured ? OK : WARN}>
                  {p.configured ? "keyed" : `missing: ${p.missing.join(", ") || "—"}`}
                </Chip>
              </div>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {p.fields.map((f) => (
                  <div key={f.key} className="space-y-1.5">
                    <Label className="text-[11px] text-muted-foreground">{f.label}</Label>
                    <Input
                      placeholder={meta.platform.values[p.key]?.[f.key] || "not set"}
                      disabled={!meta.platform.configurable}
                      value={secrets[p.key]?.[f.key] ?? ""}
                      data-testid={`payments-${p.key}-${f.key}-input`}
                      onChange={(e) => setSecrets({
                        ...secrets,
                        [p.key]: { ...(secrets[p.key] || {}), [f.key]: e.target.value },
                      })}
                      className="bg-muted/40 font-mono text-[12px]" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="border-t border-border px-4 py-2.5 text-[11px] text-muted-foreground">
          Stored encrypted at rest and shown only as the last four characters.
          {meta.platform.updated_at && ` Last changed by ${meta.platform.updated_by} on ${dateTime(meta.platform.updated_at)}.`}
        </div>
      </Panel>
    </div>
  );
};
